/*
 * email-context.ts — Outlook email selection sync, item loading, collection management.
 */

/* global Office, window, localStorage */

import { withRetry } from "./helpers";
import type { MailboxItemExt, LoadedMessageExt, AttachmentDetailsExt } from "../shared/office-compat";

// ─── Types ──────────────────────────────────

export interface CollectedEmail {
  key: string;
  itemId: string | null;
  subject: string;
  fromName: string;
  fromEmail: string;
  attachments: Office.AttachmentDetails[];
  // True when this entry's attachments come from a stale/cached fallback rather than a
  // fresh loadItemByIdAsync -- see the catch branch in syncCollectionFromSelection's
  // per-item loop. Never set true by a successful load (upsertCollectedEmail replaces the
  // whole entry, so it clears itself the next time this email's load succeeds).
  degraded?: boolean;
}

// ─── State ──────────────────────────────────

export const collectedEmails: CollectedEmail[] = [];
// Absence of a key = default-selected only for the email's own .eml (and only if it isn't
// already uploaded); everything else (attachments, local files) defaults unselected -- see
// isFileChecked below. Explicit true/false = user's own checkbox choice, which always wins.
export const selectedFiles: Record<string, boolean> = {};
export const localFiles: { id: string; file: File }[] = [];

// Shared with getEmailFileId below -- isFileChecked uses this prefix to recognize an email's
// own .eml row (the only row type auto-selected by default) without needing every caller to
// pass that distinction through separately.
const EMAIL_ITEM_ID_PREFIX = "__email_item__";

// Normalized file names (see normalizeFileNameForCompare) that the most recent live BC
// check (attachments.ts's refreshUploadedBadges) found already linked to an order. Shared
// between attachments.ts (renders the "uploaded" badge / default-unchecked checkbox) and
// customers.ts (must exclude the same files from upload candidates by default -- see
// isFileNameUploaded) so the two stay in lockstep without importing from each other.
const uploadedFileNameKeys = new Set<string>();

export function isFileNameUploaded(nameKey: string): boolean {
  return uploadedFileNameKeys.has(nameKey);
}

/** True when at least one collected email's attachments came from a degraded fallback --
 *  see CollectedEmail.degraded. Queried after every selection refresh (see
 *  renderAttachmentsCollection in attachments.ts) to show/hide a "may be incomplete,
 *  click to refresh" banner. */
export function hasDegradedEmailSelection(): boolean {
  return collectedEmails.some((e) => e.degraded);
}

export function setFileNameUploaded(nameKey: string, uploaded: boolean): void {
  if (uploaded) uploadedFileNameKeys.add(nameKey);
  else uploadedFileNameKeys.delete(nameKey);
}

// Single source of truth for "is this file row checked" -- used by both attachments.ts
// (rendering) and customers.ts (deciding upload candidates), so the two never disagree.
// selectedFiles[id] is the user's explicit choice and always wins once set (by a checkbox
// click, in either direction -- including manually re-checking an already-uploaded file to
// force a re-upload). Only when the user has never touched this file's checkbox does a
// default apply: an email's own .eml defaults checked (unless already uploaded to BC --
// still only the user can select an already-uploaded file, never auto-selection); its
// attachments and any local files always default unchecked, requiring an explicit check --
// opening an email with attachments (or dropping local files) only auto-selects the .eml.
export function isFileChecked(id: string, isUploaded: boolean): boolean {
  const explicit = selectedFiles[id];
  if (explicit !== undefined) return explicit;
  if (!id.startsWith(EMAIL_ITEM_ID_PREFIX)) return false;
  return !isUploaded;
}
let localFileCounter = 0;
let selectionRefreshRunning = false;
let selectionRefreshQueued = false;
let selectionRefreshFailureCount = 0;
const MAX_SELECTION_FAILURES = 5;

// ─── Callbacks ──────────────────────────────

type SelectionRefreshCallback = () => void;
type SelectionErrorCallback = (message: string) => void;
let onSelectionRefreshed: SelectionRefreshCallback | null = null;
let onSelectionEmpty: SelectionRefreshCallback | null = null;
let onSelectionError: SelectionErrorCallback | null = null;

export function setOnSelectionRefreshed(callback: SelectionRefreshCallback): void {
  onSelectionRefreshed = callback;
}

export function setOnSelectionEmpty(callback: SelectionRefreshCallback): void {
  onSelectionEmpty = callback;
}

export function setOnSelectionError(callback: SelectionErrorCallback): void {
  onSelectionError = callback;
}

// ─── File ID helpers ────────────────────────

export function getEmailFileId(emailKey: string): string {
  return `${EMAIL_ITEM_ID_PREFIX}${emailKey}`;
}

export function getAttachmentFileId(emailKey: string, attachmentId: string): string {
  return `__email_att__${emailKey}__${attachmentId}`;
}

function removeSelectedKeysForEmail(emailKey: string): void {
  delete selectedFiles[getEmailFileId(emailKey)];
  Object.keys(selectedFiles)
    .filter((k) => k.startsWith(`__email_att__${emailKey}__`))
    .forEach((k) => delete selectedFiles[k]);
}

// ─── Local files ────────────────────────────

export function addLocalFiles(files: FileList | File[]): void {
  const fileArray = Array.from(files);
  for (const file of fileArray) {
    const duplicate = localFiles.find(
      (f) => f.file.name === file.name && f.file.size === file.size && f.file.lastModified === file.lastModified,
    );
    if (duplicate) continue;
    if (isDuplicateOfCollectedEmailContent(file)) continue;

    const id = `__local__${Date.now()}_${localFileCounter++}`;
    localFiles.push({ id, file });
    // Deliberately does NOT stamp selectedFiles[id] = true -- see the matching note in
    // upsertCollectedEmail; leaving it absent lets isFileChecked's default apply, which for
    // a local file (never auto-selected, regardless of upload status) is always unchecked.
  }
}

function isDuplicateOfCollectedEmailContent(file: File): boolean {
  const droppedNorm = normalizeComparableName(file.name);

  for (const email of collectedEmails) {
    const emailNorm = normalizeComparableName(`${email.subject}.eml`);
    if (droppedNorm === emailNorm) return true;

    for (const att of email.attachments) {
      const attNorm = normalizeComparableName(att.name ?? "");
      const attSize = (att as AttachmentDetailsExt).size;
      if (droppedNorm === attNorm && (attSize == null || attSize === file.size)) return true;
    }
  }

  return false;
}

function normalizeComparableName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\.[a-z0-9]{2,6}$/, "") // already lowercased above; no /i needed
    .replace(/[^a-z0-9]+/g, "");
}

export function removeLocalFile(id: string): void {
  const index = localFiles.findIndex((f) => f.id === id);
  if (index < 0) return;
  localFiles.splice(index, 1);
  delete selectedFiles[id];
}

// ─── Sender emails ──────────────────────────

export function getSelectedSenderEmailSet(): Set<string> {
  const emails = collectedEmails.map((e) => (e.fromEmail ?? "").trim().toLowerCase()).filter((e) => e.length > 0);
  return new Set(emails);
}

// ─── Email collection entry ─────────────────

function getCurrentEmailCollectionEntry(): CollectedEmail | null {
  const item = Office.context.mailbox.item as unknown as MailboxItemExt | null;
  if (!item) return null;

  const subject = item.subject || "(no subject)";
  const fromName = item.from?.displayName ?? "";
  const fromEmail = item.from?.emailAddress ?? "";
  const attachments = (item.attachments ?? []).filter((a) => !a.isInline);
  const key = item.internetMessageId || item.itemId || `${subject}-${fromEmail}`;
  const itemId = item.itemId ?? null;

  return { key, itemId, subject, fromName, fromEmail, attachments };
}

function upsertCollectedEmail(entry: CollectedEmail): "added" | "updated" {
  const existingIndex = collectedEmails.findIndex((e) => e.key === entry.key);
  if (existingIndex >= 0) {
    collectedEmails[existingIndex] = entry;
  } else {
    collectedEmails.push(entry);
  }

  // Deliberately does NOT stamp selectedFiles[id] = true here: a newly-seen file id is left
  // absent from selectedFiles so isFileChecked's default (checked unless already uploaded)
  // applies. Stamping true here would look like an explicit user choice and permanently
  // override the uploaded-aware default (see isFileChecked).

  return existingIndex >= 0 ? "updated" : "added";
}

// ─── Outlook item loading ───────────────────

function getSelectedItemsAsync(): Promise<Office.SelectedItemDetails[]> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.getSelectedItemsAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value ?? []);
      } else {
        reject(new Error(result.error?.message ?? "getSelectedItemsAsync failed"));
      }
    });
  });
}

function loadItemByIdAsync(itemId: string): Promise<Office.LoadedMessageCompose | Office.LoadedMessageRead> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.loadItemByIdAsync(itemId, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? "loadItemByIdAsync failed"));
      }
    });
  });
}

/** Resolves true only if Outlook confirmed the unload -- unlike a bare resolve-on-callback,
 *  a failed unloadAsync is not silently treated as success (see releaseLoadedItem). */
function unloadLoadedItemAsync(item: Office.LoadedMessageCompose | Office.LoadedMessageRead): Promise<boolean> {
  return new Promise((resolve) => {
    item.unloadAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(true);
      } else {
        console.warn("unloadAsync failed:", result.error?.code, result.error?.message);
        resolve(false);
      }
    });
  });
}

// ─── Mail item access ───────────────────────
//
// Microsoft's item multi-select docs are explicit that loadItemByIdAsync "only processes
// one selected message at a time" and that unloadAsync must be called before another item
// can be loaded -- and that it isn't needed at all when the properties you want are already
// available. This add-in previously called it on every selection change and for every file
// read, swallowed a failed unload as if it had succeeded, and let the selection sync and an
// in-progress upload's reads race for that one slot. Reported symptom: after a while in
// classic Outlook, attachments stop appearing / reads fail with Office's generic "An
// internal error has occurred.", a task pane reload doesn't help, only restarting Outlook
// does -- consistent with that host-side slot getting stuck occupied. (Unconfirmed --
// nothing here can observe Outlook's internal state -- but these are the documented
// rules, and everything below just follows them.)
//
// withMailItem is the single way to get at an email's properties/content:
//  1. If the email is the one Outlook already has open (Office.context.mailbox.item), use
//     that directly -- no load, no slot, nothing to release. This is the normal case: the
//     manifest doesn't enable multi-select, so the selected email IS the current item.
//  2. Otherwise fall back to loadItemByIdAsync, strictly one at a time (runWithItemSlot),
//     with timeouts so a hung callback can't hold the slot forever, and an unload that
//     is verified and retried once rather than assumed.

const ITEM_LOAD_TIMEOUT_MS = 15_000;
const ITEM_UNLOAD_TIMEOUT_MS = 10_000;
const ITEM_WORK_TIMEOUT_MS = 90_000;

export interface MailItemTarget {
  itemId: string;
  internetMessageId?: string;
}

// Serializes every loadItemByIdAsync..unloadAsync cycle. Never rejects, so one failed cycle
// can't wedge every later one behind it.
let itemSlotChain: Promise<void> = Promise.resolve();

function runWithItemSlot<T>(task: () => Promise<T>): Promise<T> {
  const result = itemSlotChain.then(task);
  itemSlotChain = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

function withTimeout<T>(promise: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`${what} timed out after ${ms}ms`)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

async function releaseLoadedItem(item: Office.LoadedMessageCompose | Office.LoadedMessageRead): Promise<void> {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      if (await withTimeout(unloadLoadedItemAsync(item), ITEM_UNLOAD_TIMEOUT_MS, "unloadAsync")) return;
    } catch (err) {
      console.warn(`unloadAsync attempt ${attempt}/2 did not complete:`, err);
    }
    if (attempt < 2) await new Promise<void>((resolve) => window.setTimeout(resolve, 200));
  }
  console.warn("Could not confirm a loaded mail item was released; later loads may fail until Outlook frees it.");
}

/** Outlook's currently open item, but only if it is the email `target` refers to. */
function getCurrentItemIfMatches(target: MailItemTarget): MailboxItemExt | null {
  const item = Office.context.mailbox.item as unknown as MailboxItemExt | null;
  if (!item) return null;
  const sameItemId = !!item.itemId && item.itemId === target.itemId;
  const sameMessageId =
    !!item.internetMessageId && !!target.internetMessageId && item.internetMessageId === target.internetMessageId;
  return sameItemId || sameMessageId ? item : null;
}

/**
 * Runs `work` against the mail item `target` refers to -- see the section comment above for
 * the two ways that item is obtained. `item` is deliberately untyped (callers cast to the
 * shape they need, see office-compat.ts), since it may be either the current item or a
 * loaded one. Throws whatever `work`/the load throws; the slot is always released first.
 */
export async function withMailItem<T>(target: MailItemTarget, work: (item: unknown) => Promise<T>): Promise<T> {
  const current = getCurrentItemIfMatches(target);
  if (current) return work(current);

  return runWithItemSlot(async () => {
    const loaded = await withTimeout(loadItemByIdAsync(target.itemId), ITEM_LOAD_TIMEOUT_MS, "loadItemByIdAsync");
    try {
      return await withTimeout(work(loaded), ITEM_WORK_TIMEOUT_MS, "Reading the mail item");
    } finally {
      await releaseLoadedItem(loaded);
    }
  });
}

// ─── Selection sync ─────────────────────────

async function syncCollectionFromSelection(): Promise<void> {
  const supportsMultiSelect = Office.context.requirements.isSetSupported("Mailbox", "1.13");

  if (!supportsMultiSelect) {
    const current = getCurrentEmailCollectionEntry();
    collectedEmails.length = 0;
    Object.keys(selectedFiles)
      .filter((k) => k.startsWith(EMAIL_ITEM_ID_PREFIX) || k.startsWith("__email_att__"))
      .forEach((k) => delete selectedFiles[k]);
    if (!current) return;
    upsertCollectedEmail(current);
    return;
  }

  const selectedItems = await withRetry(() => getSelectedItemsAsync(), 2, 80);
  if (!selectedItems.length) {
    collectedEmails.length = 0;
    Object.keys(selectedFiles)
      .filter((k) => k.startsWith(EMAIL_ITEM_ID_PREFIX) || k.startsWith("__email_att__"))
      .forEach((k) => delete selectedFiles[k]);
    return;
  }

  const selectedKeys = new Set(selectedItems.map((s) => s.internetMessageId || s.itemId));
  for (let i = collectedEmails.length - 1; i >= 0; i--) {
    const key = collectedEmails[i].key;
    if (!selectedKeys.has(key)) {
      collectedEmails.splice(i, 1);
      removeSelectedKeysForEmail(key);
    }
  }

  let loadedCount = 0;
  let failedCount = 0;
  for (const s of selectedItems) {
    try {
      const details = await withRetry(
        () =>
          withMailItem({ itemId: s.itemId, internetMessageId: s.internetMessageId }, async (item) => {
            const ext = item as LoadedMessageExt;
            return {
              attachments: (ext.attachments ?? []).filter((a) => !a.isInline),
              fromName: ext.from?.displayName ?? "",
              fromEmail: ext.from?.emailAddress ?? "",
            };
          }),
        3,
        90,
      );
      upsertCollectedEmail({
        key: s.internetMessageId || s.itemId,
        itemId: s.itemId,
        subject: s.subject || "(no subject)",
        ...details,
      });
      loadedCount++;
    } catch (err) {
      console.warn("Failed to sync selected item:", s.itemId, err);
      failedCount++;
      const fallbackKey = s.internetMessageId || s.itemId;
      if (fallbackKey) {
        // A transient load failure (e.g. Outlook's host still settling right after a fast
        // selection change) shouldn't wipe out attachments/sender info already known about
        // this email from an earlier successful sync -- reuse that instead of blanking it,
        // so a flaky reload doesn't make attachments silently vanish from the list.
        const existing = collectedEmails.find((e) => e.key === fallbackKey);
        upsertCollectedEmail({
          key: fallbackKey,
          itemId: s.itemId ?? existing?.itemId ?? null,
          subject: s.subject || existing?.subject || "(no subject)",
          fromName: existing?.fromName ?? "",
          fromEmail: existing?.fromEmail ?? "",
          attachments: existing?.attachments ?? [],
          // Marks this entry so the UI can surface it (see hasDegradedEmailSelection) --
          // silently showing possibly-stale/empty attachments with no signal at all is
          // what made this look like "attachments just vanished" rather than a load that
          // failed and can be retried.
          degraded: true,
        });
        loadedCount++;
      }
    }
  }

  if (loadedCount === 0 && selectedItems.length > 0) {
    const fallbackCurrent = getCurrentEmailCollectionEntry();
    if (fallbackCurrent && selectedItems.length === 1) {
      upsertCollectedEmail(fallbackCurrent);
      loadedCount = 1;
    }
  }

  if (loadedCount === 0 && failedCount > 0) {
    throw new Error("Failed to load selected emails from Outlook context.");
  }
}

// ─── Selection refresh queue ────────────────

async function refreshSelectionState(): Promise<void> {
  try {
    await syncCollectionFromSelection();
    if (collectedEmails.length === 0) {
      if (onSelectionEmpty) onSelectionEmpty();
    } else {
      if (onSelectionRefreshed) onSelectionRefreshed();
    }
    selectionRefreshFailureCount = 0;
  } catch (err) {
    console.error("Selection sync failed:", err);
    selectionRefreshFailureCount++;
    
    if (selectionRefreshFailureCount < MAX_SELECTION_FAILURES) {
      // Retry with exponential backoff
      if (onSelectionEmpty) onSelectionEmpty();
      window.setTimeout(() => requestSelectionRefresh(), 200 * Math.pow(2, selectionRefreshFailureCount - 1));
    } else {
      // After max failures, show error and allow manual retry instead of hard reload
      handlePersistentSelectionError();
    }
  }
}

function handlePersistentSelectionError(): void {
  // Clear state to a known-good baseline
  collectedEmails.length = 0;
  Object.keys(selectedFiles)
    .filter((k) => k.startsWith(EMAIL_ITEM_ID_PREFIX) || k.startsWith("__email_att__"))
    .forEach((k) => delete selectedFiles[k]);
  
  // Notify via callback so UI can show error banner
  if (onSelectionError) {
    onSelectionError("Unable to load email selection. Click refresh or re-select emails.");
  } else if (onSelectionEmpty) {
    onSelectionEmpty();
  }
  
  // Reset failure count so user can retry
  selectionRefreshFailureCount = 0;
}

export function requestSelectionRefresh(): void {
  selectionRefreshQueued = true;
  if (selectionRefreshRunning) return;
  void processSelectionRefreshQueue();
}

async function processSelectionRefreshQueue(): Promise<void> {
  if (selectionRefreshRunning) return;
  selectionRefreshRunning = true;
  try {
    while (selectionRefreshQueued) {
      selectionRefreshQueued = false;
      await refreshSelectionState();
    }
  } finally {
    selectionRefreshRunning = false;
  }
}

// ─── Reset ──────────────────────────────────

export function resetEmailContext(): void {
  collectedEmails.length = 0;
  localFiles.length = 0;
  Object.keys(selectedFiles).forEach((k) => delete selectedFiles[k]);
}
